import {
  $createBoundTask,
  $scopedTask,
  getErrorMessage,
  getErrorScopedLabel,
} from "@captions-web/dev-logger";
import { isAbortOrCanceledError } from "@captions-web/utils/abort-signals";
import { debounce } from "@captions-web/utils/debounce";
import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { t } from "i18next";
import { nanoid } from "nanoid";
import { toast } from "sonner";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { AudioCreationEvents } from "~/lib/analytics/defs/AudioCreationEvents";
import { uploadFileToAssets } from "~/modules/assets/upload-file";
import { AudioProcessor } from "~/modules/audio/audio-processor";
import {
  TrimmableRegion,
  TrimmableRegion_Id,
} from "~/modules/audio/components/audio-trimmer/audio-trimmer";
import { MAX_AUDIO_UPLOAD_SIZE_MS, MAX_UPLOAD_DURATION_MS } from "~/modules/audio/constants";
import { urlToAudioFile } from "~/modules/audio/url-to-audio-file";
import { SampleAudio } from "~/modules/audio/utils/samples";
import { submitVoiceoverGenerationMutation } from "~/modules/speech/network/mutations";
import { voiceoverGenerationStatusQuery } from "~/modules/speech/network/queries";
import { VoiceListItemWithModelId } from "~/modules/speech/network/schemas";
import { createStoreGetters } from "~/stores/root";

import { createProjectAudioMutation } from "../network/mutations";

const DEBOUNCE_TIME = 500;
export const MAX_SCRIPT_LENGTH = 1000;
export const MIN_SCRIPT_LENGTH = 15;

type AudioSourceCreationState = {
  state: "idle" | "parsing" | "waiting" | "generating" | "editing" | "pending" | "done";
  assetId?: string;
  url?: string;
  audioProcessor?: AudioProcessor;
  durationRegions?: TrimmableRegion[];
  inputFile?: File;
  trimmedFile?: File;
  modelId?: string;
  ttsId?: string;
  sampleId?: string;
  abortController?: AbortController;
};

interface AudioCreationStoreState {
  mode: "upload" | "generate" | "record";
  sourceCreationState: AudioSourceCreationState;
  selectedVoice?: VoiceListItemWithModelId;
  script: string;
}

export function createAudioCreationStore() {
  return createStore(
    combine(
      {
        mode: "upload",
        sourceCreationState: { state: "idle" },
        script: "",
      } as AudioCreationStoreState,
      (set, get) => {
        const isScriptValid = (script: string) => {
          return (
            script.trim().length >= MIN_SCRIPT_LENGTH && script.trim().length <= MAX_SCRIPT_LENGTH
          );
        };

        const updateSourceCreationState = (newStatePartial: Partial<AudioSourceCreationState>) => {
          const existingState = get().sourceCreationState;

          set({
            sourceCreationState: {
              ...existingState,
              ...newStatePartial,
            },
          });
        };

        const resetSourceCreationState = () => set({ sourceCreationState: { state: "idle" } });

        const generateVoiceover = debounce(
          async (_p: { mode: "initial" | "retry" }) => {
            const { script, selectedVoice, sourceCreationState } = get();

            if (
              sourceCreationState.state === "generating" ||
              !isScriptValid(script) ||
              !selectedVoice
            ) {
              return;
            }

            try {
              const abortController = new AbortController();
              updateSourceCreationState({
                state: "generating",
                abortController,
                sampleId: undefined,
              });

              const $task = $createBoundTask({
                trackEvent: AudioCreationEvents.tts_generate_status.track,
                getMetadata: () => ({
                  model_id: selectedVoice.modelId,
                  voice_id: selectedVoice.id,
                  text: script,
                }),
              });

              const { operationId } = await $task("submit-generation", () =>
                submitVoiceoverGenerationMutation.trigger({
                  params: {
                    optimisticProjectId: "",
                    text: script,
                    voiceId: selectedVoice.id,
                    modelId: selectedVoice.modelId,
                    caller: "mirage",
                  },
                  signal: abortController.signal,
                })
              );

              const { promise } = voiceoverGenerationStatusQuery.beginPolling({
                params: { operationId },
                signal: abortController.signal,
              });

              abortController.signal.throwIfAborted();

              const result = await $task("poll-generation", () => promise);
              const file = await urlToAudioFile(result.url);
              const audioProcessor = new AudioProcessor();
              await audioProcessor.loadAudio(file);
              const { durationMs = 0 } = audioProcessor.metadata;
              const durationRegions = [
                {
                  startMs: 0,
                  endMs: Math.min(durationMs, MAX_AUDIO_UPLOAD_SIZE_MS),
                  index: 0,
                  id: TrimmableRegion_Id(nanoid()),
                },
              ];
              updateSourceCreationState({
                state: "editing",
                url: result.url,
                assetId: result.assetId,
                modelId: selectedVoice.modelId,
                ttsId: operationId,

                audioProcessor,
                durationRegions,
              });
            } catch (error) {
              if (!isAbortOrCanceledError(error)) {
                toast.error(t("mirage-studio:audio-creation.toasts.generate-voiceover-failed"));
              }
            }
          },
          {
            wait: DEBOUNCE_TIME,
            onPending: (pending) => {
              if (pending) {
                updateSourceCreationState({ state: "waiting" });
              }
            },
          }
        );

        const cancelInFlightAudioGeneration = (p?: { textCleared?: boolean }) => {
          const sourceCreationState = get().sourceCreationState;

          sourceCreationState.abortController?.abort();

          if (p?.textCleared) {
            generateVoiceover.cancel();
          }
        };

        const generateEventually = (p: { mode: "initial" | "retry" }) => {
          cancelInFlightAudioGeneration();

          if (get().script.trim().length > 0) {
            generateVoiceover(p);
          }
        };

        const resumeEditingAudio = () => {
          updateSourceCreationState({ state: "editing" });
        };

        return {
          async init() {},
          resetSourceCreationState,
          resumeEditingAudio,

          cancelAudioGeneration() {
            cancelInFlightAudioGeneration();
            set({ script: "" });
            resetSourceCreationState();
          },

          setMode(mode: "upload" | "generate" | "record") {
            set({ mode });
            cancelInFlightAudioGeneration();
            resetSourceCreationState();
          },

          async uploadAudioFile(p: { existingProjectId?: string }) {
            const { sourceCreationState, mode } = get();
            if (sourceCreationState.state !== "editing") {
              return;
            }
            const { durationRegions, audioProcessor } = sourceCreationState;
            if (!durationRegions || !audioProcessor) {
              return;
            }
            if (mode === "generate") {
              cancelInFlightAudioGeneration();
            }
            const { startMs, endMs } = durationRegions[0];
            const trimmedFile = audioProcessor.trimAudioFile({ startMs, endMs });
            if (!trimmedFile) {
              toast.error(t("mirage-studio:audio-creation.toasts.trim-audio-failed"));
              return;
            }

            try {
              const abortController = new AbortController();

              updateSourceCreationState({
                state: "pending",
                abortController,
              });

              const assetResponse = await $scopedTask(
                "upload-audio-file",
                () =>
                  uploadFileToAssets({
                    file: trimmedFile,
                    origin: "studio",
                    signal: abortController.signal,
                  }),
                {
                  trackEvent: AudioCreationEvents.upload_file_status.track,
                  getMetadata: () => ({ project_id: p?.existingProjectId }),
                }
              );

              updateSourceCreationState({
                state: "done",
                assetId: assetResponse.assetId,
                url: assetResponse.url,
                abortController,
              });
            } catch (error) {
              if (!isAbortOrCanceledError(error)) {
                toast.error(t("mirage-studio:audio-creation.toasts.upload-audio-failed"));
              }
            }
          },

          async handleInputFile(rawInputFile: File) {
            try {
              let inputFile = rawInputFile;
              const audioProcessor = new AudioProcessor();
              updateSourceCreationState({
                state: "parsing",
                inputFile,
                audioProcessor,
                sampleId: undefined,
                ttsId: undefined,
              });
              await audioProcessor.loadAudio(inputFile);
              let { durationMs = 0 } = audioProcessor.metadata;
              if (durationMs > MAX_UPLOAD_DURATION_MS) {
                toast.warning(t("mirage-studio:audio-creation.toasts.audio-too-long-trimming"));
                const trimmedFile = audioProcessor.trimAudioFile({
                  startMs: 0,
                  endMs: MAX_UPLOAD_DURATION_MS,
                });
                if (!trimmedFile) {
                  throw new Error("unable to trim large audio file");
                }
                await audioProcessor.loadAudio(trimmedFile);
                durationMs = MAX_UPLOAD_DURATION_MS;
                inputFile = trimmedFile;
              }
              const durationRegions = [
                {
                  startMs: 0,
                  endMs: Math.min(durationMs, MAX_AUDIO_UPLOAD_SIZE_MS),
                  index: 0,
                  id: TrimmableRegion_Id(nanoid()),
                },
              ];

              AudioCreationEvents.load_audio_status.track({
                status: "finish",
                audio_duration_ms: Math.ceil(durationMs),
              });
              updateSourceCreationState({
                state: "editing",
                inputFile,
                durationRegions,
                audioProcessor,
              });
            } catch (error) {
              if (!isAbortOrCanceledError(error)) {
                toast.error(t("mirage-studio:audio-creation.toasts.parse-audio-failed"));

                AudioCreationEvents.load_audio_status.track({
                  status: "error",
                  error_message: getErrorMessage(error),
                  error_scope: getErrorScopedLabel(error),
                });
              }
            }
          },

          async handleSampleAudioSelected(sample: SampleAudio) {
            const inputFile = await urlToAudioFile(sample.url, sample.label);

            const audioProcessor = new AudioProcessor();
            updateSourceCreationState({
              state: "parsing",
              inputFile,
              audioProcessor,
              sampleId: sample.id,
              ttsId: undefined,
            });
            await audioProcessor.loadAudio(inputFile);
            const { durationMs = 0 } = audioProcessor.metadata;

            const durationRegions = [
              {
                startMs: 0,
                endMs: Math.min(durationMs, MAX_AUDIO_UPLOAD_SIZE_MS),
                index: 0,
                id: TrimmableRegion_Id(nanoid()),
              },
            ];

            updateSourceCreationState({
              state: "editing",
              url: sample.url,
              inputFile,
              audioProcessor,
              durationRegions,
              assetId: sample.assetId,
            });

            AudioCreationEvents.sample_audio_selected.track({
              sample_id: sample.id,
              label: sample.label,
            });
          },

          setSelectedVoice(voice: VoiceListItemWithModelId) {
            const previousVoice = get().selectedVoice;

            set({ selectedVoice: voice });
            generateEventually({ mode: "initial" });

            if (previousVoice) {
              AudioCreationEvents.voice_settings_change.track({
                previous_model_id: previousVoice.modelId,
                previous_voice_id: previousVoice.id,
                new_model_id: voice.modelId,
                new_voice_id: voice.id,
              });
            }
          },
          setScript(script: string) {
            set({ script });

            if (script.trim().length > 0) {
              generateEventually({ mode: "initial" });
            } else {
              cancelInFlightAudioGeneration({ textCleared: true });
            }
          },

          async createProjectAudio(p: { existingProjectId?: string }) {
            const { mode: creation_mode, sourceCreationState } = get();
            const { assetId: audioAssetId, ttsId = null, sampleId } = sourceCreationState;
            if (sourceCreationState.state !== "done" || !audioAssetId) {
              return;
            }

            try {
              const result = await $scopedTask(
                "create-project-audio",
                () =>
                  createProjectAudioMutation.trigger({
                    params: {
                      audioAssetId,
                      projectId: p?.existingProjectId ?? null,
                      ttsId,
                    },
                  }),
                {
                  trackEvent: AudioCreationEvents.status.track,
                  getMetadata: () => ({
                    project_id: p?.existingProjectId,
                    creation_mode,
                    tts_id: ttsId ?? undefined,
                    sample_id: sampleId,
                    asset_id: audioAssetId,
                  }),
                }
              );

              return result;
            } catch (error) {
              if (!isAbortOrCanceledError(error)) {
                toast.error(t("mirage-studio:audio-creation.toasts.create-project-audio-failed"));
              }
            }
          },
          handleRegionsChange(durationRegions: TrimmableRegion[]) {
            updateSourceCreationState({ durationRegions });
          },
        };
      }
    )
  );
}

type AudioCreationStore = ReturnType<ReturnType<typeof createAudioCreationStore>["getState"]>;

export const {
  getStore: getAudioCreationStore,
  getStoreAsync: getAudioCreationStoreAsync,
  getStoreApi: getAudioCreationStoreApi,
} = createStoreGetters<AudioCreationStore>("audio-creation");

export const useAudioCreationStore = createUseStoreWithSelectorDeferred(() =>
  getAudioCreationStoreApi()
);
